<template>
  <div class="voyage-detail-wrapper">
    <div class="voyage-detail-sidebar bg-white">
      <div v-if="isLoading" class="loading-state">
        <b-spinner label="Loading..." variant="primary"></b-spinner>
        <p class="loading-text text-secondary">Loading voyage details...</p>
      </div>

      <div v-else-if="!voyage" class="not-found-state">
        <p class="text-secondary">Voyage not found.</p>
      </div>

      <div v-else class="voyage-sidebar-content p-2">
        <!-- Top Row: Info Panel + Map -->
        <div class="voyage-top-row mb-3">
          <!-- Voyage Information -->
          <VoyageInfoPanel
            :voyage="voyage"
            :assets="assets"
            :landmarks="landmarks"
          />

          <!-- Map -->
          <VoyageMap
            :voyage="voyage"
            :gps-data="gpsData"
            :asset="currentAsset"
            :origin-landmark="originLandmark"
            :destination-landmark="destinationLandmark"
            :loading="isMapLoading"
            @map-initialized="onMapInitialized"
            @track-drawn="onTrackDrawn"
            @ship-added="onShipAdded"
            @dot-trail-added="onDotTrailAdded"
          />
        </div>

        <!-- Voyage Summary -->
        <VoyageSummary
          v-if="summary"
          :voyage="voyage"
          :summary="summary"
        />

        <!-- Performance Charts -->
        <PerformanceCharts
          :rpm-data="rpmData"
          :flowmeter-data="flowmeterData"
          :gps-data="gpsData"
          :loading="isChartsLoading"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useVoyageData } from '@/views/voyage/composables/useVoyageData';
import VoyageInfoPanel from '@/views/voyage/components/VoyageInfoPanel.vue';
import VoyageMap from '@/views/voyage/components/VoyageMap.vue';
import VoyageSummary from '@/views/voyage/components/VoyageSummary.vue';
import PerformanceCharts from '@/views/voyage/components/PerformanceCharts.vue';

// Store reference to composable functions
let fetchTelemetryDataFromComposable;

export default {
  name: 'VoyageDetail',
  components: {
    VoyageInfoPanel,
    VoyageMap,
    VoyageSummary,
    PerformanceCharts,
  },
  setup() {
    const {
      loadAllVoyageData,
      loadTelemetryData,
      fetchLandmarkById,
    } = useVoyageData();

    // Store reference to the composable function
    fetchTelemetryDataFromComposable = loadTelemetryData;

    return {
      loadAllVoyageData,
      fetchLandmarkById,
    };
  },
  data() {
    return {
      voyage: null,
      summary: null,
      gpsData: null,
      rpmData: null,
      flowmeterData: null,
      assets: [],
      landmarks: [],
      originLandmark: null,
      destinationLandmark: null,
      isLoading: false,
      isMapLoading: true,
      isChartsLoading: true,
    };
  },
  computed: {
    currentAsset() {
      if (!this.voyage?.mvygMassId) return null;
      return this.assets.find((a) => a.massId === this.voyage.mvygMassId);
    },
  },
  async mounted() {
    await this.loadVoyageData();
    await this.loadLandmarks();
  },
  methods: {
    async loadVoyageData() {
      try {
        this.isLoading = true;
        this.isMapLoading = true;
        this.isChartsLoading = true;

        // Load voyage data, summary, assets, and landmarks
        const {
          voyage, summary, assets, landmarks,
        } = await this.loadAllVoyageData({
          voyageId: this.$route.params.id,
          store: this.$store,
        });

        this.voyage = voyage;
        this.summary = summary;
        this.assets = assets;
        this.landmarks = landmarks;

        if (!voyage) {
          this.$toast.error('Voyage not found');
          return;
        }

        // Voyage master + summary loaded - show the data immediately
        this.isLoading = false;

        // Load telemetry data (GPS, RPM, Flowmeter)
        await this._fetchTelemetryData();
      } catch (error) {
        console.error('loadVoyageData error:', error);
        this.$toast.error('Failed to load voyage details');
        this.isLoading = false;
        this.isMapLoading = false;
        this.isChartsLoading = false;
      }
    },
    async loadLandmarks() {
      if (!this.voyage) return;

      const originId = this.voyage.mvygOriginLndmId;
      const destId = this.voyage.mvygDestinationLndmId;

      if (!originId && !destId) return;

      try {
        const [origin, destination] = await Promise.all([
          originId ? this.fetchLandmarkById(originId) : null,
          destId ? this.fetchLandmarkById(destId) : null,
        ]);

        this.originLandmark = origin;
        this.destinationLandmark = destination;
      } catch (error) {
        console.error('loadLandmarks error:', error);
      }
    },
    async _fetchTelemetryData() {
      try {
        if (!this.voyage) {
          console.warn('_fetchTelemetryData: voyage is not available');
          return;
        }

        const { gpsData, rpmData, flowmeterData } = await fetchTelemetryDataFromComposable(this.voyage);

        this.gpsData = gpsData;
        this.rpmData = rpmData;
        this.flowmeterData = flowmeterData;

        // Map loading is handled by VoyageMap component
        this.isMapLoading = false;
        this.isChartsLoading = false;
      } catch (error) {
        console.error('_fetchTelemetryData error:', error);
        this.isMapLoading = false;
        this.isChartsLoading = false;
      }
    },
    onMapInitialized(map) {
      console.log('[VoyageDetail] Map initialized:', map);
    },
    onTrackDrawn({ coordinatesData }) {
      console.log('[VoyageDetail] Track drawn with', coordinatesData.length, 'points');
    },
    onShipAdded() {
      console.log('[VoyageDetail] Ship added to map');
    },
    onDotTrailAdded({ markersAdded }) {
      console.log('[VoyageDetail] Dot trail added with', markersAdded, 'markers');
    },
  },
};
</script>

<style scoped>
.voyage-detail-wrapper {
  width: 100%;
}

.voyage-detail-sidebar {
  width: 100%;
}

.voyage-detail-sidebar::-webkit-scrollbar {
  display: none;
}

.voyage-top-row {
  display: grid;
  grid-template-columns: 35% 65%;
  gap: 0.75rem;
  align-items: stretch;
}

@media (max-width: 991.98px) {
  .voyage-top-row {
    grid-template-columns: 1fr;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 300px;
  gap: 1rem;
}

.not-found-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}
</style>
